/**
 * Webconfig API — Centralized port of AngularJS webconfigDataService.js
 *
 * Source: public/SPIGlassApp/services/webconfigDataService.js
 *
 * Previously inlined across 6+ files. Now centralized here with caching
 * to avoid duplicate API calls for the same static config values.
 */

import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';

const API_BASE = `${buildApiBase()}/API/Partial`;

// ── Simple in-memory cache (these values never change during a session) ──

let _imageViewerUrl: string | null = null;
let _imageSrcPrefix: string | null = null;
let _fileImporterUrl: string | null = null;

// ── API Functions (actively used — ported from 6+ inlined locations) ──

/**
 * GET /API/Partial/GetImageViewerURL/
 * Returns the base URL for the external image viewer application.
 * Cached for the session (value never changes).
 *
 * Previously inlined in: AppSubNav.tsx, AddBidRoute.tsx, BidDispositionRoute.tsx,
 * UiImageGallery.tsx, damagesApi.ts, auditDashboardApi.ts
 */
export async function getImageViewerURL(): Promise<string> {
  if (_imageViewerUrl != null) return _imageViewerUrl;
  const response = await axiosClient.get<string>(`${API_BASE}/GetImageViewerURL/`);
  _imageViewerUrl = response.data ?? '';
  return _imageViewerUrl;
}

/**
 * GET /API/Partial/GetImageViewerSrcPrefix
 * Returns the prefix for constructing image source URLs (e.g., "https://images.sgpdev.com/").
 * Cached for the session.
 *
 * Previously inlined in: UiImageGallery.tsx, ResultsRoute.tsx, damagesApi.ts, overallowablesApi.ts
 */
export async function getImageViewerSrcPrefix(): Promise<string> {
  if (_imageSrcPrefix != null) return _imageSrcPrefix;
  const response = await axiosClient.get<string>(`${API_BASE}/GetImageViewerSrcPrefix`, {
    silent401: true,
  });
  _imageSrcPrefix = response.data ?? '';
  return _imageSrcPrefix;
}

/**
 * GET /API/Partial/GetFileImporterURL
 * Returns the URL for the file importer application (SafeSend).
 * Cached for the session.
 *
 * Previously inlined in: AppSubNav.tsx
 */
export async function getFileImporterURL(): Promise<string> {
  if (_fileImporterUrl != null) return _fileImporterUrl;
  const response = await axiosClient.get<string>(`${API_BASE}/GetFileImporterURL`);
  _fileImporterUrl = response.data ?? '';
  return _fileImporterUrl;
}

/**
 * GET /API/Partial/GetRCTAlwaysEnableKey
 * AngularJS: webconfigDataService.getRCTAlwaysEnableKey() (webconfigDataService.js:123-128)
 * Returns the boolean web.config flag that force-enables the RCT Call Out
 * button regardless of audit counts. Not cached — AngularJS does not pass
 * `{ cache: true }`.
 */
export async function getRCTAlwaysEnableKey(): Promise<boolean> {
  const response = await axiosClient.get<boolean>(`${API_BASE}/GetRCTAlwaysEnableKey`, {
    silent401: true,
  });
  return Boolean(response.data);
}

// ── Shell-only methods (consumed by AngularJS shell, not React routes) ──
// These are documented here for completeness. They are NOT ported because
// they are only consumed by AngularJS shell components (header, sidebar,
// footer, dashboard). If the shell is ever fully migrated to React, these
// endpoints are available on the spiglassapi server.
//
// getHelpURL()                   → GET /API/Partial/GetHelpURL/
// getFeedbackEmail()             → GET /API/Partial/GetFeedbackEmail/
// getCurrentUsername()           → GET /API/Partial/GetCurrentUsername/
// getMaxDateCurrent()            → GET /API/Partial/getMaxDateCurrent/
// getWorkFlowStateIDsForTasks()  → GET /API/Partial/getWorkFlowStateIDsForTasks/
// getWorkFlowStateCodesForTasks()→ GET /API/Partial/getWorkFlowStateCodesForTasks/
// getPhaseVOnlyPermissions()     → GET /API/Partial/GetPhaseVOnlyPermissions/
//
// ── generalDataService methods not ported (consumed by AngularJS only) ──
// These methods are defined in generalDataService.js but are NOT called by any
// React route. They are either bridged via AngularJS injector (CompleteOrder)
// or consumed exclusively by AngularJS controllers/services.
//
// getWorkOrderInfoFromSubmissionId() → GET /API/Submission/GetWorkOrder (by parentSubmissionId)
// CompleteOrder()                    → GET /API/Submission/CompleteSubmission OR /API/WorkOrderInfo/CompleteOrder
//                                      (bridged via AngularJS injector in AppSidebar.tsx line 1931)
// getPhase()                         → GET /API/Submission/GetPhase (by clientCode)
// getPhaseWithOrderNumber()          → GET /API/Submission/GetPhaseWithOrderNumber (by orderNumber)
// getLanguagePack()                  → GET /API/Script/GetLanguagePack?language=EN
//                                      (called by AngularJS index.js; React reads from localStorage via deCompressLanguagePackData)
// getCTDV2Supported()                → GET /API/Client/ConfiguredForCTV2 (by loanType, cached)
